import * as echarts from 'echarts';
import debounce from 'lodash-es/debounce';
import React, { useEffect, useMemo, useRef } from 'react';
import { Skeleton } from '@fourthwall/components';
import { DEFAULT_SERIES_STYLES, colorsMap } from './LineChart.consts';
import * as S from './LineChart.styled';
import { formatValue } from './utils/formatValue';
const ANIMATION_DURATION = 600;
export const LineChart = ({ data, isLoading = false, showAxes = true, tooltipFormatter, yAxisFormatter, xAxisFormatter, seriesStyles = DEFAULT_SERIES_STYLES, animation = true, appearance = 'dark', }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const colors = useMemo(() => {
        return colorsMap[appearance];
    }, [appearance]);
    useEffect(() => {
        if (chartRef.current && !chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current, undefined, {
                renderer: 'svg',
            });
            // Delay ResizeObserver setup until animation completes
            const setupResizeObserver = () => {
                const debouncedResize = debounce(() => {
                    chartInstance.current?.resize();
                }, 0);
                const resizeObserver = new ResizeObserver(() => {
                    debouncedResize();
                });
                resizeObserver.observe(chartRef.current);
                return resizeObserver;
            };
            let resizeObserver = null;
            // If animation is enabled, wait for it to complete before setting up ResizeObserver
            if (animation) {
                // Wait for initial animation to complete
                const timer = setTimeout(() => {
                    resizeObserver = setupResizeObserver();
                }, ANIMATION_DURATION);
                return () => {
                    clearTimeout(timer);
                    resizeObserver?.disconnect();
                    chartInstance.current?.dispose();
                    chartInstance.current = null;
                };
            }
            // If no animation, setup ResizeObserver immediately
            resizeObserver = setupResizeObserver();
            return () => {
                resizeObserver?.disconnect();
                chartInstance.current?.dispose();
                chartInstance.current = null;
            };
        }
    }, [animation]);
    const xAxisLabels = useMemo(() => data[0]?.data.map((p) => p.label) ?? [], [data]);
    const series = useMemo(() => {
        return data.map((serie, i) => {
            const style = seriesStyles[i] || DEFAULT_SERIES_STYLES[i % DEFAULT_SERIES_STYLES.length];
            const opacity = Math.round((style.areaStyle?.opacity ?? 0.15) * 255)
                .toString(16)
                .padStart(2, '0');
            return {
                name: serie.label,
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 4,
                showSymbol: false,
                emphasis: {
                    scale: true,
                    focus: 'series',
                },
                data: serie.data.map((dataPoint) => dataPoint.value),
                lineStyle: {
                    color: style.color,
                    width: style.width,
                    type: style.lineStyle || 'solid',
                    zIndex: i === 0 ? 9 : 0,
                },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            { offset: 0, color: `${style.areaStyle?.color ?? style.color}${opacity}` },
                            { offset: 1, color: `${style.areaStyle?.color ?? style.color}00` },
                        ],
                    },
                },
                itemStyle: { color: style.color },
                animationEasing: 'cubicOut',
                animationDuration: ANIMATION_DURATION,
            };
        });
    }, [data, seriesStyles]);
    const chartOptions = useMemo(() => ({
        showSpinner: isLoading,
        animation,
        tooltip: {
            trigger: 'axis',
            backgroundColor: 'transparent',
            borderWidth: 0,
            extraCssText: 'box-shadow:none;padding:0',
            formatter: (tooltipParams) => {
                const label = formatValue(tooltipParams[0]?.axisValue, tooltipFormatter?.xAxisFormatter, xAxisFormatter);
                const tooltipSeries = tooltipParams
                    .map((serieTooltipParams) => {
                    const value = formatValue(serieTooltipParams.value, tooltipFormatter?.yAxisFormatter, yAxisFormatter);
                    return `
              <div style="${S.TooltipItemContainerStyles}">
                <div style="${S.TooltipItemPointStyles}; background: ${serieTooltipParams.color}; display: ${tooltipParams.length > 1 ? 'block' : 'none'}"></div>
                <div style="${S.TooltipItemContentStyles}">
                  <div style="${S.TooltipItemLabelStyles}">${label}</div>
                  <div style="${S.TooltipItemValueStyles}">${value}</div>
                </div>
              </div>`;
                })
                    .join('');
                return `
            <div style="${S.TooltipStyles}">
              ${tooltipSeries}
            </div>
          `;
            },
        },
        grid: {
            top: 10,
            bottom: 0,
            left: 0,
            right: 0,
            containLabel: showAxes,
        },
        xAxis: {
            type: 'category',
            data: xAxisLabels,
            boundaryGap: false,
            show: showAxes,
            axisLine: { lineStyle: { color: colors.axisLine } },
            axisTick: { show: false },
            axisLabel: {
                show: showAxes,
                formatter: (value) => formatValue(value, xAxisFormatter),
                color: colors.axisLabel,
                fontSize: 12,
                interval: 'auto',
                rotate: 0,
                hideOverlap: true,
                width: 60,
                overflow: 'break',
                showMinLabel: false,
                showMaxLabel: false,
            },
        },
        yAxis: {
            type: 'value',
            min: 0,
            show: showAxes,
            axisLine: { show: false },
            splitLine: { show: showAxes, lineStyle: { color: colors.splitLine } },
            axisLabel: {
                show: showAxes,
                formatter: (value) => formatValue(value, yAxisFormatter),
                color: colors.axisLabel,
                fontSize: 12,
                margin: 16,
            },
        },
        series: series.reverse(), // reverses the order so the most recent data appears at the top
    }), [
        isLoading,
        animation,
        showAxes,
        xAxisLabels,
        xAxisFormatter,
        yAxisFormatter,
        series,
        colors,
        tooltipFormatter?.xAxisFormatter,
        tooltipFormatter?.yAxisFormatter,
    ]);
    useEffect(() => {
        if (chartInstance.current && !isLoading) {
            chartInstance.current.setOption(chartOptions);
        }
    }, [chartOptions, isLoading]);
    if (isLoading) {
        return (React.createElement(S.ChartContainer, null,
            React.createElement(Skeleton.Shape, { width: "100%", height: "100%" })));
    }
    if (!data.length || !data[0].data.length) {
        return (React.createElement(S.ChartContainer, null,
            React.createElement(S.LoadingContainer, null, "No data")));
    }
    return (React.createElement(S.ChartContainer, null,
        React.createElement(S.ChartDiv, { ref: chartRef })));
};
